import { PanelRightClose, PanelRightOpen } from 'lucide-react';

interface Props {
  /** `open` sits on the page's right edge while the panel is closed; `close`
      hangs off the open panel's outer (left) edge at the same height. */
  action: 'open' | 'close';
  visible: boolean;
  onClick: () => void;
  title: string;
  ariaLabel?: string;
  className?: string;
}

/**
 * The edge tab that toggles a right-hand panel (#237).
 *
 * Both states use the same small tab at the same height, so the panel opens
 * and closes from one spot, and the close control needs no header row of its
 * own. It sits outside the panel, on its edge, so it reads as belonging to the
 * whole panel rather than to the first section's heading.
 *
 * The `close` tab must be rendered inside the panel's positioned wrapper; the
 * `open` tab inside the page's positioned container.
 */
export function PanelEdgeTab({ action, visible, onClick, title, ariaLabel, className = '' }: Props) {
  const place = action === 'open' ? 'right-0' : 'right-full';
  // Slides toward the panel as it hides: the open tab off the page edge, the
  // close tab back under the panel.
  const hidden = action === 'open' ? 'translate-x-full' : 'translate-x-6';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel ?? title}
      aria-hidden={visible ? undefined : true}
      tabIndex={visible ? undefined : -1}
      className={`flex absolute ${place} top-1 z-10 items-center justify-center w-6 h-8 bg-bg-secondary border border-r-0 border-border rounded-l-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-all duration-200 ${
        visible ? 'translate-x-0 opacity-100' : `${hidden} opacity-0 pointer-events-none`
      } ${className}`}
    >
      {action === 'open' ? <PanelRightOpen size={14} /> : <PanelRightClose size={14} />}
    </button>
  );
}
